import { useMap } from '@vis.gl/react-google-maps'
import { FeatureCollection, GeoJsonProperties, Point, BBox } from 'geojson'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import Supercluster, { ClusterProperties } from 'supercluster'

interface ISuperclusterHookResult<T extends GeoJsonProperties> {
  clusters: (Supercluster.ClusterFeature<Supercluster.ClusterProperties> | Supercluster.PointFeature<T>)[]
  getChildren: (
    clusterId: number
  ) => (Supercluster.ClusterFeature<Supercluster.ClusterProperties> | Supercluster.PointFeature<T>)[]
  getLeaves: (clusterId: number) => Supercluster.PointFeature<T>[]
  getClusterExpansionZoom: (clusterId: number) => number
}

export function useSupercluster<T extends GeoJsonProperties>(
  geojson: FeatureCollection<Point, T>,
  superclusterOptions: Supercluster.Options<T, ClusterProperties>
): ISuperclusterHookResult<T> {
  // create the clusterer and keep it
  const clusterer = useMemo(() => {
    return new Supercluster(superclusterOptions)
  }, [superclusterOptions])

  // version-number for the data loaded into the clusterer
  // (this is needed to trigger updating the clusters when data was changed)
  const [version, dataWasUpdated] = useReducer((x: number) => x + 1, 0)

  // when data changes, load it into the clusterer
  useEffect(() => {
    clusterer.load(geojson.features)
    dataWasUpdated()
  }, [clusterer, geojson])

  // get bounding-box and zoomlevel from the map
  const { bbox, zoom } = useMapViewport({ padding: 100 })

  // retrieve the clusters within the current viewport
  const clusters = useMemo(() => {
    // don't try to read clusters before data was loaded into the clusterer (version===0),
    // otherwise getClusters will crash
    if (!clusterer || version === 0) return []

    return clusterer.getClusters(bbox, zoom)
  }, [version, clusterer, bbox, zoom])

  // create callbacks to expose supercluster functionality outside of this hook
  const getChildren = useCallback((clusterId: number) => clusterer.getChildren(clusterId), [clusterer])

  // note: here, the paging that would be possible is disabled; we found this
  // has no significant performance impact when it's just used in a click event handler.
  const getLeaves = useCallback((clusterId: number) => clusterer.getLeaves(clusterId, Infinity), [clusterer])

  const getClusterExpansionZoom = useCallback(
    (clusterId: number) => clusterer.getClusterExpansionZoom(clusterId),
    [clusterer]
  )

  return {
    clusters,
    getChildren,
    getLeaves,
    getClusterExpansionZoom
  }
}

type MapViewportOptions = {
  padding?: number
}

interface IMapViewportHookResult {
  bbox: BBox
  zoom: number
}

export function useMapViewport({ padding = 0 }: MapViewportOptions = {}): IMapViewportHookResult {
  const map = useMap()
  const [bbox, setBbox] = useState<BBox>([-180, -90, 180, 90])
  const [zoom, setZoom] = useState(0)

  // observe the map to get current bounds
  useEffect(() => {
    if (!map) return

    const listener = map.addListener('bounds_changed', () => {
      const bounds = map.getBounds()
      const zoom = map.getZoom()
      const projection = map.getProjection()

      if (!bounds || !zoom || !projection) return

      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()

      const paddingDegrees = degreesPerPixel(zoom) * padding

      const n = Math.min(90, ne.lat() + paddingDegrees)
      const s = Math.max(-90, sw.lat() - paddingDegrees)

      const w = sw.lng() - paddingDegrees
      const e = ne.lng() + paddingDegrees

      setBbox([w, s, e, n])
      setZoom(zoom)
    })

    return () => listener.remove()
  }, [map, padding])

  return { bbox, zoom }
}

function degreesPerPixel(zoomLevel: number): number {
  // 360° divided by the number of pixels at the zoom-level
  return 360 / (Math.pow(2, zoomLevel) * 256)
}
